/**************************************
 ** AtlasAPIImageAdapter
 *
 *  Resolves an image index into a path from Green Lantern Product API spec
 *  ex: var adapter = new AtlasAPIPathImageAdapter('http://atlas-content.turbosquid.com/assets/8f8c9bc8-a026-46ea-a815-530c9a4fc27b/index.json');
 *
 ***************************************/
define('atlas/AtlasAPIImageAdapter', ['atlas/AtlasClientCapabilities', 'atlas/AtlasConstants', 'atlas/AtlasPreloader'], function(AtlasClientCapabilities, Constants, AtlasPreloader) {

    'use strict';

    var AtlasAPIImageAdapter = function(serverConfiguration) {
        this._assetId = 0;
        this._data = {};
        this._latitudes = 0;
        this._longitudes = 0;
        this._validLatitudes = [];
        this._validLongitudes = [];
        this._url = '';
        this._downloadFormats = [];
        this._previewHosts = [];
        this._previewImages = [];
        this._selectedResolution = {};
        this._imageHosts = [];
        this._imagePaths = {};
        this._staticImagePaths = {};
        this._staticPreviewImages = [];
        this._configuration = [];
        this._defaultConfiguration = [];
        this._compositors = [];
        this._compositorExtension = 'jpg';
        this._previewImageSize = null; //no preference specified
        this._serverConfiguration = serverConfiguration || null;
        this._isDefaultConfiguration = true;
        this._isCustomConfiguration = false;

        this._pixelSquidIndexDownloadFormats = [];
    };

    AtlasAPIImageAdapter.prototype.getData = function() {
        return this._data;
    };

    AtlasAPIImageAdapter.prototype.loadFromV02JSON = function(data) {
        this._assetId = data.guid;
        this._latitudes = data.atlas.latitudes;
        this._longitudes = data.atlas.longitudes;

        //load the preview images
        if (!data.previews || !data.previews.items || !data.previews.items.length) {
            //no preview images found
        } else {
            for (var p = 0; p < data.previews.items.length; ++p) {
                if (data.previews.items[p].view) {
                    this._previewImages.push(data.previews.items[p].view);
                }
            }
        }

        //make sure there is at least one host
        var views = data.downloadableViews.jpeg600;
        var hostCount = views.root.length;
        if (hostCount < 1) {
            //no root specified for jpeg600 images
            return false;
        }

        var extension = views.ext || 'jpg';
        var hostIndex = 0;
        this._imagePaths = {};

        var polesRendered = data.atlas.renderPoles;

        //going to fill these to be backward compatible, but won't use them in the v02 loading code
        this._validLatitudes = Constants.latitudeMarkers.slice(0, this._latitudes);
        this._validLongitudes = Constants.longitudeMarkers.slice(0, this._longitudes);

        for (var lat = 0; lat < (this._latitudes); ++lat) {
            for (var lon = 0; lon < (this._longitudes); ++lon) {
                //skip over non rendered poles
                if (((lat === 0) || (lat === (this._latitudes - 1))) && (!polesRendered) && (lon > 0)) {
                    continue;
                }

                var view = Constants.latitudeMarkers[lat] + Constants.longitudeMarkers[lon];
                var host = views.root[(hostIndex++) % hostCount];
                //associated view to path
                this._imagePaths[view] = host + '/' + view + '.' + extension;
            }
        }

        return true;
    };

    //_arrayIntersection
    //
    // return the intersection of two arrays
    //
    //  input:
    //      array1 - an array
    //      array2 - an array
    //
    //  output:
    //      the intersection of the two arrays
    //
    AtlasAPIImageAdapter.prototype._arrayIntersection = function (array1, array2) {
        var iterator1 = 0, //loop control variable for array1
            iterator2 = 0, //loop control variable for array2
            output = []; //output array

        array1 = array1 || [];
        array2 = array2 || [];

        for (iterator1 = 0; iterator1 < array1.length; iterator1++) {
            if (array2.indexOf(array1[iterator1]) >=0) {
                output.push(array1[iterator1]);
            }
        }

        return output;
    };

    //_convertIndexesToAvailableLatitudesAndLongitudes
    //
    // take the extensions.atlas.indexes.index from the atlas data structure and convert it into an array
    // of latitudes and longitudes that can be loaded.
    //
    // the array of latitude and longitudes will contain a union of the latitudes and intersection of longitudes.
    // for example if the structure contains B01, C01, C02, E01, E03 the result would be latitudes = B, C, E and
    // longitudes = 01.  if the structure contains B01, C02, E03 the result would be latitudes = B, C, E and
    // longitudes = empty (since there is no intersection of longitudes)
    //
    //  input:
    //      index - an object with properties defining existing of individual cells (lat+lon) - { A01: 1, B01:1, B02:1}
    //      excludeLatitudes - an array of latitudes that should be excluded from return (probably poles) - [A, B]
    //
    //  output:
    //      { latitudes: [A, B, C, ...], longitudes: ['01', '02', '03', ...] }
    //
    AtlasAPIImageAdapter.prototype._convertIndexesToAvailableLatitudesAndLongitudes = function (index, excludeLatitudes) {
        var item, //the item in index we are currently reviewing
            latitude, //current latitude in loop
            outerLongitude, //current outer longitude loop control variable
            innerLongitude, //current inner longitude loop control variable
            latitudes = [], //array of latitudes, each containing an ordered array of longitudes
            firstTime = true, //if first time through the latitude list
            output = {}; // output of the method

        //split a structure index: { A01: 1, B02: 1, ... } into an array of latitudes [A, B, ...] and longitudes
        //[01, 02, ...]
        for (item in index) {
            if (index.hasOwnProperty(item)) {
                //parse out latitude
                latitude = item.substring(0, 1);

                //don't do anything with excluded latitudes
                if (excludeLatitudes && excludeLatitudes.indexOf(latitude) >= 0) {
                    continue;
                }

                if (!latitudes[latitude]) {
                    //if we don't yet have an entry for this latitude then create a new array
                    latitudes[latitude] = [];
                    latitudes[latitude].push(item.substring(1));
                }
                else {
                    //add the current longitude to the current latitude array - we should not be receiving
                    //duplicate longitude entries for a latitude
                    latitudes[latitude].push(item.substring(1));
                }
            }
        }

        output.longitudes = [];
        output.latitudes = [];

        //construct the intersection of the longitudes
        for (latitude in latitudes) {
            if (latitudes.hasOwnProperty(latitude)) {
                //going to have a minimum threshold to prevent random images from other latitudes
                //causing problems
                var longitudes = latitudes[latitude].slice(0);
                if (longitudes.length < 4) {
                    continue;
                }

                output.latitudes.push(latitude);

                if (firstTime) {
                    //save the first latitude
                    item = latitude;
                    //copy the first latitude longitudes
                    output.longitudes = longitudes;
                    //no longer first time
                    firstTime = false;
                    continue;
                }

                //go through each longitude in the first latitude, and keep it only if it is found in the current
                //longitude list
                output.longitudes = this._arrayIntersection(output.longitudes, latitudes[latitude]);
            }
        }

        //our lists are currently unordered, so sort (based alphabetically for latitudes, and numberical for longitudes)
        output.latitudes.sort();
        output.longitudes.sort(function(a, b) { return a - b; });

        return output;
    };

    //_selectValidLatitudes
    //
    //  for a non-configurable asset we are picking the most restrictive between:
    //      the latitudes from the index, or
    //      the latitudes specified in the limitLatitudes parameter to this method
    //  for a configurable asset we are picking:
    //      if it is being loaded in non-configurator mode then previous rule applies
    //      if loading in configurator mode, then we want most restrictive between:
    //          the latitudes from the index
    //          the availalble latitudes from the object
    //          the latitudes specified in the limitLatitudes parameter to this method
    //
    //  input:
    //      indexLatitudes - latitudes specified in the index
    //      availableLatitudes - latitudes specified in the object itself
    //      limitLatitudes - latitudes specified elsewhere (like in a configuration object, or on page)
    //
    //  output:
    //      valid latitudes to load - [A, B, C, D]
    //
    AtlasAPIImageAdapter.prototype._selectValidLatitudes = function(indexLatitudes, limitLatitudes, availableLatitudes) {
        var output = {}, //valid latitudes that should be loaded
            excludeIndex = 0, //loop control variable for excludes
            excludePosition; //position in the output where the exclude value was found

        if ((!limitLatitudes || limitLatitudes.length === 0) && (!availableLatitudes || availableLatitudes.length === 0)) {
            return indexLatitudes;
        }

        if ((!availableLatitudes) || (availableLatitudes.length === 0)) {
            //if no available latitudes, then we are assuming a non-configurable asset
            output = this._arrayIntersection(indexLatitudes, limitLatitudes);
        }
        else {
            output = this._arrayIntersection(indexLatitudes, availableLatitudes);
            if (limitLatitudes) {
                output = this._arrayIntersection(output, limitLatitudes);
            }
        }

        return output;
    };

    //_selectValidLongitudes
    //
    //  construct a series of longitudes that is constrained by the longitude downsample (how many longitude
    //  to skip between longitudes) basing off the index longitudes
    //
    //  input:
    //      indexLongitudes - longitudes constructed from the index
    //      downsample - the number of latitudes to skip
    //
    //  output:
    //      valid longitudes to load
    //
    AtlasAPIImageAdapter.prototype._selectValidLongitudes = function(indexLongitudes, downsample, longitudes) {
        var output = [], //output of longitudes
            longitudeIndex = 0; //loop control variable when going through longitudes

        //default longitudes if not set
        longitudes = longitudes || 16;

        if (downsample && (downsample > 1)) {
            for (longitudeIndex = 0; longitudeIndex < indexLongitudes.length; longitudeIndex += downsample) {
                output.push(Constants.longitudeMarkers[longitudeIndex]);
            }

            if (indexLongitudes) {
                output = this._arrayIntersection(indexLongitudes, output);
            }
        } else {
            output = Constants.longitudeMarkers.slice(0, longitudes);

            if (indexLongitudes) {
                output = this._arrayIntersection(indexLongitudes, output);
            }
        }

        return output;
    };

    //_construct2dPreviewImageUrls
    //
    // take the data.assets.previews and construct the preview image urls for the non-3d (non-atlas)
    // previews.  These should contain FQDN urls.
    //
    //  input:
    //      previews - the data.assets.previews array in the v03 asset structure
    // 
    //  output:
    //      [ {baseUrl: full url to thumbnail, largeUrl: full url to large image}, ...]
    //
    AtlasAPIImageAdapter.prototype._construct2dPreviewImageUrls = function(previews) {
        if (!previews || !previews.length) {
            return [];
        }

        var previewImages = [],
            p = 0;

        for (p = 0; p < previews.length; ++p) {
            //skip anything that is not a 2d image
            if (previews[p].type === '2d') {
                previewImages.push({baseUrl: previews[p].resolutions.res60x60.path, largeUrl:previews[p].resolutions.res500x500.path});
            }
        }

        return previewImages;
    };

    //_constructPixelSquidShotPreviewUrls
    //
    // pixelsquid assets will have shotsPreview and shots ordered exactly the same
    //
    AtlasAPIImageAdapter.prototype._constructPixelSquidShotPreviewUrls = function(shotsPreview, shots, shotEndpoints) {
        /* jshint camelcase:false */
        var output = {
            previews: [],
            resolutions: {}
        };

        for (var p = 0; p < shotsPreview.length; ++p) {
            output.previews.push({
                tag: shotsPreview[p].id,
                baseUrl: shotsPreview[p].url
            });

            output.resolutions[shotsPreview[p].id] = {
                available: shots[p].formats,
                baseUrl: shotsPreview[p].url,
                largeUrl: shotEndpoints[p % shotEndpoints.length] + '/' + shots[p].media[0].path
            };
        }

        return output;
    };

    //_constructShotPreviewImageUrls
    //
    // take the data.assets.previews and the data.extensions.shots.shots array and construct the thumbnails and
    // preview image urls.
    //
    // input:
    //     previews - array of previews in the data.assets.previews v03 asset structure
    //     previewEndpoints - an array of endpoints to be used with the previews (already fixed for https)
    //     shots - array of shots in the data.extensions.shots.shots v06 asset structure
    //     shotEndpoints - an array of endpoints to be used with the shots
    //     size - the size of the thumbnail that should be used in previews
    //
    // output:
    //     { 
    //       previews: [ {tag: tag of image, baseUrl: full url to thumbnail}, ...],
    //       resolutions:  tag: tag of image -> { baseUrl: full url to thumbnail, largeUrl: full url to large image }, ... }
    //     }
    AtlasAPIImageAdapter.prototype._constructShotPreviewImageUrls = function(previews, previewEndpoints, shots, shotEndpoints, size) {
        /* jshint camelcase:false */
        var output = {
            previews: [],
            resolutions: {}
        };

        if (!previews || !previews.length || !shots || !shots.length) {
            return output;
        }

        if (!size) {
            size='res160x160';
        }

        for (var p = 0; p < previews.length; ++p) {
            if (previews[p].type === "shots") {
                output.previews.push({
                    tag: previews[p].id,
                    baseUrl: previewEndpoints[0] + '/' + previews[p].resolutions[size].name
                });

                output.resolutions[previews[p].id] = {
                    available: [],
                    baseUrl: previewEndpoints[0] + '/' + previews[p].resolutions[size].name
                };
            }
        }

        var shotHosts = [];
        if (this._serverConfiguration && this._serverConfiguration.useMultipleHosts) {
            var replacements = (document.location.protocol === 'https:' ? this._serverConfiguration.replacementSSLHosts : this._serverConfiguration.replacementHosts);

            for (var hostCount = 0; hostCount < replacements.length; hostCount++) {
                shotHosts.push(shotEndpoints[0].replace(this._serverConfiguration.rootHostForReplacement,
                    replacements[hostCount]));
            }

        }
        else {
            shotHosts = shotEndpoints;
        }

        //detect if we are in SSL and change the scheme
        if (document.location.protocol === 'https:') {
            for (var h = 0; h < shotHosts.length; ++h) {
                shotHosts[h] = shotHosts[h].replace('http:', 'https:');
            }
        }

        var host = 0;
        for (var s = 0; s < shots.length; ++s) {
            //make sure we have a preview for it
            if (output.resolutions[shots[s].preview_id]) {
                for (var m = 0; m < shots[s].media.length; ++m) {
                    //add to the available formats
                    output.resolutions[shots[s].preview_id].available.push({format: shots[s].media[m].format, resolution: shots[s].media[m].resolution});

                    if (shots[s].media[m].format === 'jpeg' && shots[s].media[m].resolution === '600') {
                        output.resolutions[shots[s].preview_id].largeUrl = shotHosts[host] + '/' + shots[s].media[m].path;
                    }
                }
            }
            host = (host + 1) % shotHosts.length;
        }


        return output;
    };

    //_constructSpinnerPreviewUrl
    AtlasAPIImageAdapter.prototype._constructPixelSquidSpinnerPreviewUrl = function(spinnerPreview) {
        return [{
            baseUrl: spinnerPreview.url,
            customizedUrl: spinnerPreview.url,
            view: spinnerPreview.view
        }];
    };

    //_constructStaticPreviewImageUrls
    //
    // take the data.assets.previews and array of endpoints and construct the preview image urls for
    // the base configuration
    //
    //  input:
    //      previews - the data.assets.previews array in the v03 asset structure
    //      endpoints - an array of endpoints (could be from data.assets.endpoints.previews)
    //      size - the size key to use (optional), otherwise will try to load the 160x160 or 64x64 if not available
    //              if a size is specified then it will only return that specific size, it will not just pick something
    //              close
    //      latitudes - a list of valid latitudes that will be matched against to make we are only pulling
    //              atlas previews that are on valid latitudes (optional)
    //      longitudes - a list of valid longitudes (optional)
    //
    //  output:
    //      [ {view: A01, baseUrl: full url to view }, ...]
    //
    AtlasAPIImageAdapter.prototype._constructStaticPreviewImageUrls = function(previews, endpoints, size, latitudes, longitudes) {
        //make sure we have previews
        if (!previews || !previews.length) {
            return [];
        }

        //make sure we have endpoints
        if (!endpoints || !endpoints.length) {
            return [];
        }

        var previewImages = [], //output structure for preview images
            p = 0,  //loop control variable
            latitude, //the latitude of the current view
            longitude; //the longitude of the current view

        for (p = 0; p < previews.length; ++p) {
            //skip anything that is not an atlas image
            if (((previews[p].type === 'atlas') && (previews[p].view)) || ((!previews[p].type) && (previews[p].view))) {
                latitude = previews[p].view.substring(0, 1);
                longitude = previews[p].view.substring(1);

                //pull out view latitudes that do not exist in our valid latitudes
                if (latitudes && latitudes.indexOf(latitude) < 0) {
                    continue;
                }

                //pull out view longitudes that do not exist in our valid longitudes
                if (longitudes && longitudes.indexOf(longitude) < 0) {
                    continue;
                }

                if (!size) {
                    if (previews[p].resolutions.res160x160 || previews[p].resolutions.res64x64) {
                        previewImages.push({
                            view: previews[p].view,
                            //need to support multiple at somepoint, also need to figure out process of selecting thumbnail sizes
                            baseUrl: (previews[p].resolutions.res160x160 ?
                                endpoints[0] + '/' + previews[p].resolutions.res160x160.name :
                                endpoints[0] + '/' + previews[p].resolutions.res64x64.name),
                            //for backwards compatibility
                            customizedUrl:  (previews[p].resolutions.res160x160 ?
                                endpoints[0] + '/' + previews[p].resolutions.res160x160.name :
                                endpoints[0] + '/' + previews[p].resolutions.res64x64.name)
                        });
                    }
                }
                else {
                    if (previews[p].resolutions[size]) {
                        previewImages.push({
                            view: previews[p].view,
                            //need to support multiple at somepoint, also need to figure out process of selecting thumbnail sizes
                            baseUrl: endpoints[0] + '/' + previews[p].resolutions[size].name,
                            //for backwards compatibility
                            customizedUrl:  endpoints[0] + '/' + previews[p].resolutions[size].name
                        });
                    }
                }
            }
        }

        return previewImages;
    };

    //_constructConfigurablePreviewImageUrls
    //
    // take the data.assets.previews and array of endpoints and construct the preview image urls for
    // the base configuration
    //
    //  input:
    //      previews - the data.assets.previews array in the v03 asset structure
    //      enpoints - an array of endpoints (could be from data.assets.endpoints.previews)
    //      extension - extension to be used for images
    //      configurationUrl - the currently selected configuration as url parameters
    //      latitudes - a list of valid latitudes that will be matched against to make we are only pulling
    //              atlas previews that are on valid latitudes (optional)
    //      longitudes - a list of valid longitudes (optional)
    //
    //  output:
    //      [ {view: A01, baseUrl: full url to view }, ...]
    //
    AtlasAPIImageAdapter.prototype._constructConfigurablePreviewImageUrls = function(previews, endpoints, size, extension, configurationUrl, latitudes, longitudes) {
        //make sure we have previews
        if (!previews || !previews.length) {
            return [];
        }

        //make sure we have endpoints
        if (!endpoints || !endpoints.length) {
            return [];
        }

        var previewImages = [], //output structure for preview images
            p = 0,  //loop control variable
            hostIndex = 0, //host index
            latitude, //latitude of the current view
            longitude; //longitude of the current view

        for (p = 0; p < previews.length; ++p) {
            //skip anything that is not an atlas image
            if (((previews[p].type === 'atlas') && (previews[p].view)) || ((!previews[p].type) && (previews[p].view))) {
                latitude = previews[p].view.substring(0, 1);
                longitude = previews[p].view.substring(1);

                //pull out view latitudes that do not exist in our valid latitudes
                if (latitudes && latitudes.indexOf(latitude) < 0) {
                    continue;
                }

                //pull out view longitudes that do not exist in our valid longitudes
                if (longitudes && longitudes.indexOf(longitude) < 0) {
                    continue;
                }

                //if the size was not specified, then just add it, but it if was specified, only add matching ones
                //so that this has functional parity with the non-configurable ones
                if (!size) {
                    previewImages.push({
                        view: previews[p].view,
                        //need to support multiple at somepoint, also need to figure out process of selecting thumbnail sizes
                        baseUrl: endpoints[hostIndex % endpoints.length] + '/' + previews[p].view + '.' + extension + '?' + configurationUrl,
                        //for backwards compatibility
                        customizedUrl: endpoints[hostIndex % endpoints.length] + '/' + previews[p].view + '.' + extension + '?' + configurationUrl
                    });

                    hostIndex++;
                }
                else {
                    if (previews[p].resolutions[size]) {
                        previewImages.push({
                            view: previews[p].view,
                            //need to support multiple at somepoint, also need to figure out process of selecting thumbnail sizes
                            baseUrl: endpoints[hostIndex % endpoints.length] + '/' + previews[p].view + '.' + extension + '?' + configurationUrl,
                            //for backwards compatibility
                            customizedUrl: endpoints[hostIndex % endpoints.length] + '/' + previews[p].view + '.' + extension + '?' + configurationUrl
                        });

                        hostIndex++;
                    }
                }
            }
        }

        return previewImages;
    };

    //_constructDownloadFormats
    //
    // take the data.extensions.atlas.indexes[i].index object and construct the available download files
    // for the specified format and resolution
    //
    //  input:
    //      indexes - the object containin { format: jpg, resolution: 600, indexes: { A01: 1, B01: 1, ... } } for the available formats
    //
    //  output:
    //      { A01: ['jpg-300', ...], B01: ['jpg-600', 'jpg-2k', ...], }
    //
    AtlasAPIImageAdapter.prototype._constructDownloadFormats = function(indexes) {
        var downloadFormats = [], //output structure with download formats for each specific view
            index = 0, //loop control variable for each available index
            view, //loop control variable for each view in the index
            currentFormatResolution; //the current format and resolution we are inspecting

        //go through each set of indexes available
        for (index = 0; index < indexes.length; ++index) {
            currentFormatResolution = { format: indexes[index].format, resolution: indexes[index].resolution };

            //for each view in the index
            for (view in indexes[index].index) {
                if (indexes[index].index.hasOwnProperty(view)) {
                    //create a new array if we are not already tracking that view
                    if (!downloadFormats[view]) {
                        downloadFormats[view] = [];
                    }

                    downloadFormats[view].push(currentFormatResolution);
                }
            }
        }

        return downloadFormats;
    };

    //_constructDefaultConfiguration
    //
    // take the data.customizations object and populate the array of objects that contains the first selection for
    // each option
    //
    //  input:
    //      customizations - an array of objects that have codes, names, and arrays of choices
    //
    //  output:
    //      [ { option: 'XXX', choice: 'XXXX' }, etc]
    //
    AtlasAPIImageAdapter.prototype._constructDefaultConfiguration = function(customizations) {
        var output = [], //output structure containing choices
            parameter; //loop control variable for parameters

        for (parameter = 0; parameter < customizations.length; ++parameter) {
            output.push({
                option: customizations[parameter].code,
                choice: customizations[parameter].options[0].code
            });
        }

        return output;
    };

    //_configurationsAreEqual
    //
    // take two configurations and compare to see if they are the same
    //
    // input:
    //      configuration1 - first configuration to compare
    //      configuration2 - second configuration to compare
    //
    //  output:
    //      true if they are the same, false if not
    //
    AtlasAPIImageAdapter.prototype._configurationsAreEqual = function(configuration1, configuration2) {
        //early out test
        if (configuration1.length !== configuration2.length) {
            return false;
        }

        //make copies of the arrays
        var c1 = configuration1.slice(0),
            c2 = configuration2.slice(0),
            configurationSort = function(a, b) {
                if (a.option < b.option) {
                    return -1;
                }
                if (a.option > b.option) {
                    return 1;
                }

                return 0;
            };


        c1.sort(configurationSort);
        c2.sort(configurationSort);

        for (var c = 0; c < c1.length; ++c) {
            if ((c1[c].option !== c2[c].option) || (c1[c].choice !== c2[c].choice)) {
                return false;
            }
        }

        return true;
    };

    //_constructConfigurableUrlParameters
    //
    // take a configuration and return the url parameters necessary to pass to the compositor
    //
    // input:
    //      configuration - the current configuration
    //
    //  output:
    //      the url parameters, without the ?, to pass to the compositor
    //
    AtlasAPIImageAdapter.prototype._constructConfigurableUrlParameters = function(configuration) {
        var configurationUrl = '', //the configuration as a set of url parameters
            optionIndex = 0; //the option loop control variable

        for (optionIndex = 0; optionIndex < configuration.length; ++optionIndex) {
            configurationUrl += 'cust[' + configuration[optionIndex].option + ']=' + configuration[optionIndex].choice;
            if (optionIndex !== (configuration.length-1)) {
                configurationUrl += '&';
            }
        }

        return configurationUrl;
    };

    //_selectPixelSquidImageResolution
    //
    // uses the single pixelsquid resolution returned and converts it in to compatible formats for use in the image index
    //
    // * for each index available, calculate the available latitudes and longitudes
    //
    //  input:
    //      atlas - extensions.atlas object in the asset object
    //      limitLatitudes - the latitudes being limited (from configuration or page)
    //
    //  output:
    //      selected resolution - { index: N, format: 'YYYY', resolution: 'XXXX', available: { latitudes: [], longitudes: [] }, score: ZZZZ }
    //          or null if no suitable resolution can be found
    //
    AtlasAPIImageAdapter.prototype._selectPixelSquidImageResolution = function(atlas, limitLatitudes) {
        /* jshint camelcase:false */
        var polesRendered = atlas.poles_rendered || false, //whether or not the poles were rendered
            availableLatitudes = (atlas.available_latitudes && atlas.available_latitudes.length) ? atlas.available_latitudes : null, //configurable latitudes
            longitudeDownsample = atlas.longitude_downsample || 1, //the longitude downsample
            latitudes = atlas.latitudes || 16, //the number of latitudes
            longitudes = atlas.longitudes || 16, //the number of longitudes
            excludeLatitudes = []; //the latitudes to exclude from the loading routine

        if (!polesRendered) {
            excludeLatitudes.push(Constants.latitudeMarkers[0]);
            excludeLatitudes.push(Constants.latitudeMarkers[latitudes-1]);
        }

        //pixelsquid contains a single index
        var available = this._convertIndexesToAvailableLatitudesAndLongitudes(atlas.indexes[0].index, excludeLatitudes);

        //update the latitudes and longitudes to only contain what is available and should be shown
        //if we are loading as a configurator then pass available latitudes
        available.latitudes = this._selectValidLatitudes(available.latitudes, limitLatitudes, null);
        available.longitudes = this._selectValidLongitudes(available.longitudes, longitudeDownsample, longitudes);

        return {
            excludeLatitudes: excludeLatitudes,
            index: 0,
            format: atlas.indexes[0].format,
            resolution: atlas.indexes[0].resolution,
            endpoints: atlas.indexes[0].endpoints,
            extension: atlas.indexes[0].ext,
            available: available,
            score: 0
        };
    };

    //_selectImageResolutionFromPreferences
    //
    // takes the information from extensions.atlas and selects the appropriate image resolution and format
    // based on the preferences passed in.  the algorithm is as follows:
    //
    // * for each index available, calculate the available latitudes and longitudes, and store the lats * longs as the score
    // * if the first image preference matches the format and resolution and has the highest score then return it
    // * sort the array by score (lat * lon)
    // * loop through the index scores
    // *    loop through the preferences
    // *        if the preference matches the index then choose it
    //
    //  input:
    //      atlas - extensions.atlas object in the asset object
    //      limitLatitudes - the latitudes being limited (from configuration or page)
    //      imagePreferences - array of image preferences order by top preference first
    //
    //  output:
    //      selected resolution - { index: N, format: 'YYYY', resolution: 'XXXX', available: { latitudes: [], longitudes: [] }, score: ZZZZ }
    //          or null if no suitable resolution can be found
    //
    AtlasAPIImageAdapter.prototype._selectImageResolutionFromPreferences = function(atlas, limitLatitudes, imagePreferences, loadAsConfigurator) {
        /* jshint camelcase:false */
        var polesRendered = atlas.poles_rendered || false, //whether or not the poles were rendered
            loadAsConfigurator = loadAsConfigurator || false, //whether we are loading as a configurator or not
            selectedPreference = selectedPreference || { resolution: null, format: null }, //the selected preference,
            availableLatitudes = (atlas.available_latitudes && atlas.available_latitudes.length) ? atlas.available_latitudes : null, //configurable latitudes
            longitudeDownsample = atlas.longitude_downsample || 1, //the longitude downsample
            latitudes = atlas.latitudes || 16, //the number of latitudes
            longitudes = atlas.longitudes || 16, //the number of longitudes
            excludeLatitudes = [], //the latitudes to exclude from the loading routine
            preferenceIndex = 0, //the current preference index
            index = 0, //the current index in the indexes array
            resolutions = [], //the resolutions
            highestScore = -10000, //save the highest matching score (lat * lon)
            highestIndex = -1, //the index with the highest matching score
            highestScoreIsExact = false; //if the highest score was a match (we can stop searching)

        //setup default image preference if not there
        imagePreferences = imagePreferences || [{ resolution: '600', format: 'jpeg' }];

        //if poles are not rendered then add the first and last latitudes to the exclude list
        if (!polesRendered) {
            excludeLatitudes.push(Constants.latitudeMarkers[0]);
            excludeLatitudes.push(Constants.latitudeMarkers[latitudes-1]);
        }

        for (index = 0; index < atlas.indexes.length; index++) {
            //fill first set of resolutions with the latitudes and longitudes from the index
            resolutions.push({
                excludeLatitudes: excludeLatitudes,
                index: index,
                format: atlas.indexes[index].format,
                resolution: atlas.indexes[index].resolution,
                endpoints: atlas.indexes[index].endpoints,
                extension: atlas.indexes[index].ext,
                available: this._convertIndexesToAvailableLatitudesAndLongitudes(atlas.indexes[index].index, excludeLatitudes),
                score: 0
            });

            //update the latitudes and longitudes to only contain what is available and should be shown
            //if we are loading as a configurator then pass available latitudes
            resolutions[index].available.latitudes = this._selectValidLatitudes(resolutions[index].available.latitudes,
                limitLatitudes, loadAsConfigurator ? availableLatitudes : null);
            resolutions[index].available.longitudes = this._selectValidLongitudes(resolutions[index].available.longitudes,
                longitudeDownsample, longitudes);

            //update the score
            resolutions[index].score=resolutions[index].available.latitudes.length*resolutions[index].available.longitudes.length;

            //we will just compare the first image preference here, if we find an exact match with the highest score then we
            //will not need to continue
            if (resolutions[index].score >= highestScore) {
                highestScore = resolutions[index].score;
                highestIndex = index;
                highestScoreIsExact = (resolutions[index].format === imagePreferences[0].format) &&
                    (resolutions[index].resolution === imagePreferences[0].resolution);
            }
        }

        //if the highest score found was an exact match then we can just return the highest score index
        if (highestScoreIsExact) {
            return resolutions[highestIndex];
        }

        //here we just need to pick something suitable, sort the array descending by score and match
        resolutions.sort(function(a, b) { return b.score - a.score; });
        for (index = 0; index < resolutions.length; index++) {
            for (preferenceIndex = 0; preferenceIndex < imagePreferences.length; preferenceIndex++) {
                if ((resolutions[index].format === imagePreferences[preferenceIndex].format) &&
                    (resolutions[index].resolution === imagePreferences[preferenceIndex].resolution)) {

                    return resolutions[index];
                }
            }
        }

        return null;
    };

    //_constructViewUrls
    //
    // based on the latitudes and longitudes that have been determined, construct an object that contains
    // the image url fo reach view
    //
    // input:
    //      indexLatitudes - an array of valid latitudes to load
    //      indexLongitudes - an array of valid longitudes to load
    //      endpoints - a list of endpoints that will be used to load the views
    //      extention - optional (if other than jpg)
    //      extra - optional, will be appended to query string, probably a configuration url
    //
    // output:
    //      imagePaths - an associative array of view to url
    //
    AtlasAPIImageAdapter.prototype._constructViewUrls = function(indexLatitudes, indexLongitudes, endpoints, extension, extra) {
        var hostIndex = 0, //loop control variable when going through hosts
            imagePaths = {}, //output structure containing URLs for each view
            latitude = 0, //loop control variable for latitudes
            longitude = 0, //loop control variable for longitude
            view, //constructed view index
            host; //host value for current view

        if (endpoints.length < 1) {
            //no endpoints found for images'
            return null;
        }
        extension = extension || 'jpg';

        if (!this._modifiedAt) {
            this._modifiedAt='1';
        }

        for (latitude = 0; latitude < indexLatitudes.length; ++latitude) {
            for (longitude = 0; longitude < indexLongitudes.length; ++longitude) {
                view = indexLatitudes[latitude] + indexLongitudes[longitude];
                host = endpoints[(hostIndex++) % endpoints.length];

                //associated view to host
                imagePaths[view] = host + '/' + view + '.' + extension + (extra ? '?' + extra + '&modifiedAt=' + this._modifiedAt : '?modifiedAt=' + this._modifiedAt);
            }
        }

        return imagePaths;
    };

    //_constructPreviewHosts
    //
    // take a single host in an array and construct an array of multiple hosts based on the constants.  this will
    // check the configuration file to see this is turned on or not.  these hosts should not contain a trailing
    // slash.
    //
    // input:
    //      endpoints - the data.assets.endpoints object
    //
    // output:
    //      hosts - an array of hosts
    //
    AtlasAPIImageAdapter.prototype._constructPreviewHosts = function(endpoints) {
        var hosts = [],  //the hosts that are returned
            hostCount = 0, //the loop control variable while iterating through the hosts
            replacements = []; //replacement list for the hosts

        replacements = (document.location.protocol === 'https:' ? this._serverConfiguration.replacementSSLHosts : this._serverConfiguration.replacementHosts);

        if (this._serverConfiguration.useMultipleHosts && (endpoints.previews.length === 1)) {
            for (hostCount = 0; hostCount < replacements.length; hostCount++) {
                hosts.push(endpoints.previews[0].replace(this._serverConfiguration.rootHostForReplacement,
                    replacements[hostCount]));
            }

            //trim the trailing slash if needed
            for (var h = 0; h < hosts.length; ++h) {
                if (hosts[h].substr(hosts[h].length - 1, 1) === '/') {
                    hosts[h] = hosts[h].substr(0, hosts[h].length - 1);
                }
            }
        }
        else {
            for (var h = 0; h < endpoints.previews.length; ++h) {
                if (endpoints.previews[h].substr(endpoints.previews[h].length - 1, 1) === '/') {
                    hosts.push(endpoints.previews[h].substr(0, endpoints.previews[h].length - 1));
                }
                else {
                    hosts.push(endpoints.previews[h]);
                }
            }
        }

        //detect if we are in SSL and change the scheme
        if (document.location.protocol === 'https:') {
            for (var h = 0; h < hosts.length; ++h) {
                hosts[h] = hosts[h].replace('http:', 'https:');
            }
        }

        return hosts;
    };

    //_constructImageHosts
    //
    // take a single host in an array and construct an array of multiple hosts based on the constants.  this will
    // check the configuration file to see this is turned on or not
    //
    // input:
    //      endpoints - the data.assets.endpoints object
    //
    // output:
    //      hosts - an array of hosts
    //
    AtlasAPIImageAdapter.prototype._constructImageHosts = function(endpoints) {
        var hosts = [],  //the hosts that are returned
            hostCount = 0, //the loop control variable while iterating through the hosts
            replacements = []; //the replacement list for the hosts

        replacements = (document.location.protocol === 'https:' ? this._serverConfiguration.replacementSSLHosts : this._serverConfiguration.replacementHosts);

        if (this._serverConfiguration.useMultipleHosts && (endpoints.length === 1)) {
            for (hostCount = 0; hostCount < replacements.length; hostCount++) {
                hosts.push(endpoints[0].replace(this._serverConfiguration.rootHostForReplacement,
                    replacements[hostCount]));
            }
        }
        else {
            hosts = endpoints;
        }

        //detect if we are in SSL and change the scheme
        if (document.location.protocol === 'https:') {
            for (var h = 0; h < hosts.length; ++h) {
                hosts[h] = hosts[h].replace('http:', 'https:');
            }
        }

        return hosts;
    };

    //_constructCompositorHosts
    //
    // take a single host in an array and construct an array of multiple hosts based on the constants.  this will
    // check the configuration file to see if we are dev or prod, and whether this is turned on or not
    //
    // input:
    //      endpoints - the data.assets.endpoints object
    //
    // output:
    //      hosts - an array of hosts
    //
    AtlasAPIImageAdapter.prototype._constructCompositorHosts = function(endpoints) {
        var hosts = [],  //the hosts that are returned
            hostCount = 0, //the loop control variable while iterating through the hosts
            compositors = []; //the compositor list to use as replacements

        compositors = this._serverConfiguration.replacementCompositors;

        if (this._serverConfiguration.useMultipleCompositors && (endpoints.compositor.length === 1)) {
            for (hostCount = 0; hostCount < compositors.length; hostCount++) {
                hosts.push(endpoints.compositor[0].replace(this._serverConfiguration.rootCompositorForReplacement,
                    compositors[hostCount]));
            }
        }
        else {
            hosts = [endpoints.compositor];
        }

        //detect if we are in SSL and change the scheme
        if (document.location.protocol === 'https:') {
            for (var h = 0; h < hosts.length; ++h) {
                hosts[h] = hosts[h].replace('http:', 'https:');
            }
        }

        return hosts;
    };


    AtlasAPIImageAdapter.prototype._loadFromV030JSONUsingConfiguration = function(data, loadAsConfigurator, serverConfiguration) {
        /* jshint camelcase:false */
        var capabilities = AtlasClientCapabilities.getCapabilities(); //the client capabilities

        this._data = data;
        this._assetId = data.id;

        //pull the modified_at time
        this._modifiedAt = data.modified_at || data.updated_at;
        if (!this._modifiedAt) {
            this._modifiedAt = '1';
        }

        //must at least have these keys to load
        if (!data.extensions || (!data.extensions.atlas && !data.extensions.shots)) {
            return false;
        }

        this._latitudes = data.extensions.atlas.latitudes;
        this._longitudes = data.extensions.atlas.longitudes;

        if (this._latitudes && this._longitudes && data.extensions.atlas.indexes && data.extensions.atlas.indexes.length) {
            //store the download files
            this._downloadFormats = this._constructDownloadFormats(data.extensions.atlas.indexes);

            //select the resolution
            this._selectedResolution = this._selectImageResolutionFromPreferences(data.extensions.atlas, capabilities.latitudeSubset,
                capabilities.imagePreferences, loadAsConfigurator);

            //copy the latitudes and longitudes from the selected resolution
            this._validLatitudes = this._selectedResolution.available.latitudes.slice(0);
            this._validLongitudes = this._selectedResolution.available.longitudes.slice(0);

            //construct the image hosts for the selected resolution
            this._imageHosts = this._constructImageHosts(this._selectedResolution.endpoints);

            //construct the image paths for the default configuration (these are all static images)
            this._imagePaths = this._constructViewUrls(this._validLatitudes, this._validLongitudes, this._imageHosts,
                this._selectedResolution.extension);

            //create another pointer to the static image paths
            this._staticImagePaths = this._imagePaths;
        }

        //get the static hosts
        this._previewHosts = this._constructPreviewHosts(data.assets.endpoints);

        //load the preview images
        if (data.assets.previews && data.assets.previews.length) {
            this._previewImages = this._constructStaticPreviewImageUrls(data.assets.previews, this._previewHosts,
                this._previewImageSize, this._validLatitudes, this._validLongitudes);
            //create another pointer to the static preview images
            this._staticPreviewImages = this._previewImages;

            //create a pointer to the 2d images (if there are any)
            this._static2dPreviewImages = this._construct2dPreviewImageUrls(data.assets.previews);
        }

        if (data.assets.previews && data.assets.previews.length && data.extensions.shots && data.extensions.shots.shots && data.extensions.shots.shots.length) {
            //create a pointer to the shots
            this._shotPreviewImages = this._constructShotPreviewImageUrls(data.assets.previews, this._previewHosts, data.extensions.shots.shots,
                data.extensions.shots.endpoints, 'res160x160');

            //we need to prefetch the large images
            var shotPreloader = new AtlasPreloader();
            for (var i = 0; i < this._shotPreviewImages.previews.length; ++i) {
                var imageUrl = this.getLargeImageForShot(this._shotPreviewImages.previews[i].tag);
                shotPreloader.addImage(imageUrl);
            }
            shotPreloader.load();
        }

        //if there is a customizations section and we are allowing the loading of the configurator
        //then we need to enable the customizations object
        if (data.customizations && data.customizations.length && capabilities.loadConfigurator) {
            //store the default configuration as the current configuration
            this._configuration = this._constructDefaultConfiguration(data.customizations);

            //store a copy of the default configuration
            this._defaultConfiguration = this._configuration;
            //need to store endpoints and extension for constructing urls in the future
            this._compositors = this._constructCompositorHosts(data.assets.endpoints);
            this._compositorExtension = this._selectedResolution.extension;
        }

        return true;
    };

    AtlasAPIImageAdapter.prototype._loadFromPixelSquidJSONUsingConfiguration = function(data, loadAsConfigurator, serverConfiguration) {
        /* jshint camelcase:false */
        var capabilities = AtlasClientCapabilities.getCapabilities(); //the client capabilities

        this._data = data;
        this._assetId = data.id;

        //pull the modified_at time
        this._modifiedAt = data.modified_at || data.updated_at;
        if (!this._modifiedAt) {
            this._modifiedAt = '1';
        }

        //must at least have these keys to load
        if (!data.extensions || (!data.extensions.atlas && !data.extensions.shots)) {
            return false;
        }

        this._latitudes = data.extensions.atlas.latitudes;
        this._longitudes = data.extensions.atlas.longitudes;

        if (this._latitudes && this._longitudes && data.extensions.atlas.indexes && data.extensions.atlas.indexes.length) {
            //store the download files
            this._pixelSquidIndexDownloadFormats = data.extensions.atlas.formats;

            //select the resolution
            this._selectedResolution = this._selectPixelSquidImageResolution(data.extensions.atlas, capabilities.latitudeSubset);

            //copy the latitudes and longitudes from the selected resolution
            this._validLatitudes = this._selectedResolution.available.latitudes.slice(0);
            this._validLongitudes = this._selectedResolution.available.longitudes.slice(0);

            //for pixelsquid this comes directly from the asset structure
            this._imageHosts = this._selectedResolution.endpoints;

            //construct the image paths for the default configuration (these are all static images)
            this._imagePaths = this._constructViewUrls(this._validLatitudes, this._validLongitudes, this._imageHosts,
                this._selectedResolution.extension);

            //create another pointer to the static image paths
            this._staticImagePaths = this._imagePaths;
        }

        //get the static hosts - not used in pixelsquid
        this._previewHosts = null;

        //load the preview images
        if (data.spinner_previews && data.spinner_previews.length) {
            this._previewImages = this._constructPixelSquidSpinnerPreviewUrl(data.spinner_previews[0]);
            //create another pointer to the static preview images
            this._staticPreviewImages = this._previewImages;
        }

        if (data.shot_previews && data.extensions.shots && data.extensions.shots.shots && data.extensions.shots.shots.length) {
            //create a pointer to the shots
            this._shotPreviewImages = this._constructPixelSquidShotPreviewUrls(data.shot_previews, data.extensions.shots.shots,
                data.extensions.shots.endpoints);

            //we need to prefetch the large images
            var shotPreloader = new AtlasPreloader();
            for (var i = 0; i < this._shotPreviewImages.previews.length; ++i) {
                var imageUrl = this.getLargeImageForShot(this._shotPreviewImages.previews[i].tag);

                shotPreloader.addImage(imageUrl);
            }
            shotPreloader.load();
        }

        if (data.small_images) {
            this._smallImages = data.small_images;
        }

        return true;
    };

    AtlasAPIImageAdapter.prototype.loadFromV030JSON = function(data, loadAsConfigurator, serverConfiguration) {
        var that = this; //store the pointer for the callback

        //if a server configuration is passed in then use it over the base version
        this._serverConfiguration = serverConfiguration || this._serverConfiguration;
        //if the server configuration is still null, then load the defaults from the library
        if (!this._serverConfiguration) {
            require(['atlas/AtlasServerConfiguration'], function(configuration) {
                that._serverConfiguration = configuration;
                return that._loadFromV030JSONUsingConfiguration(data, loadAsConfigurator, that._serverConfiguration);
            });
        }
        else {
            return this._loadFromV030JSONUsingConfiguration(data, loadAsConfigurator, this._serverConfiguration);
        }
    };

    AtlasAPIImageAdapter.prototype.loadFromPixelSquidJSON = function(data, loadAsConfigurator, serverConfiguration) {
        this._serverConfiguration = serverConfiguration || this._serverConfiguration;

        if (!this._serverConfiguration) {
            var that = this;
            require(['atlas/AtlasServerConfiguration'], function(configuration) {
                that._serverConfiguration = configuration;
                return that._loadFromPixelSquidJSONUsingConfiguration(data, loadAsConfigurator, that._serverConfiguration);
            });
        }
        else {
            return this._loadFromPixelSquidJSONUsingConfiguration(data, loadAsConfigurator, this._serverConfiguration);
        }
    };

    AtlasAPIImageAdapter.prototype.loadFromJSON = function(data, loadAsConfigurator, serverConfiguration) {
        /* jshint camelcase:false */
        if (!this._url) {
            this._url = 'local';
        }

        if (typeof data.player_schema_version !== 'undefined') {
            return this.loadFromPixelSquidJSON(data, false, serverConfiguration);
        }

        //check the version of the json, we can currently load from 0.2, 0.3.0, 0.4.1, and 0.6.0
        var version = data.version || data.schema_version;

        var isSupportedVersion = false;
        var supportedVersions = ['0.2', '0.3.0', '0.4.1', '0.6.0'];
        for (var i = 0; i < supportedVersions.length; ++i) {
            isSupportedVersion = (version === supportedVersions[i]);
            if (isSupportedVersion) {
                break;
            }
        }
        if (!isSupportedVersion) {
            return false;
        }

        if (version === '0.2') {
            if (!this.loadFromV02JSON(data)) {
                //could not load data for version 0.2
                return false;
            }
        }
        else {
            if (!this.loadFromV030JSON(data, loadAsConfigurator, serverConfiguration)) {
                //could not load data for version 0.3.0, 0.4.1, 0.6.0
                return false;
            }
        }

        return true;
    };

    AtlasAPIImageAdapter.prototype.loadFromURL = function(url, loadAsConfigurator, serverConfiguration, callback, object) {
        if ((typeof jQuery === "undefined") || (!jQuery)) {
            if ((typeof console !== 'undefined') && (console)) {
                console.log('jQuery is not loaded and cannot be used to retrieve asset');
            }
            return;
        }

        if (document.location.protocol === 'https:') {
            url = url.replace('http:', 'https:');
        }

        this._url = url;

        var obj = this;

        jQuery.ajax({
            type: 'GET',
            data: null,
            url: obj._url,
            dataType: 'jsonp',
            jsonpCallback: 'hero_player',
            contentType: 'application/json',
            success: function(data) {
                //there is no option to override loading as configurator for assets pulled from server
                var success = obj.loadFromJSON(data, loadAsConfigurator, serverConfiguration);
                callback(object, success);
            },
            error: function(xhr, status, error) {
                //GET Product API failed
                callback(object, false);
            }
        });
    };

    AtlasAPIImageAdapter.prototype.load = function(url, loadAsConfigurator, serverConfiguration, callback, object) {
        this.loadFromURL(url, loadAsConfigurator, serverConfiguration, callback, object);
    };

    AtlasAPIImageAdapter.prototype.isConfigurator = function() {
        return (this._configuration && (this._configuration.length > 0));
    };

    AtlasAPIImageAdapter.prototype.isDefaultConfiguration = function() {
        return this._isDefaultConfiguration;
    };

    AtlasAPIImageAdapter.prototype.isCustomConfiguration = function() {
        return this._isCustomConfiguration;
    };

    AtlasAPIImageAdapter.prototype.applyConfiguration = function(configuration) {
        var configurationUrl; //the constructed url parameters for this configuration

        //check to see if the incomming configuration is the same as the current configuration
        if (this._configurationsAreEqual(this._configuration, configuration)) {
            return;
        }
        //check to see if the incomming configuration is the default configuration, and just load the
        //static images if so
        if (this._configurationsAreEqual(this._defaultConfiguration, configuration)) {
            this._configuration = this._defaultConfiguration;
            this._imagePaths = this._staticImagePaths;
            this._previewImages = this._staticPreviewImages;

            this._isDefaultConfiguration = true;
            this._isCustomConfiguration = false;
            return;
        }

        //oterwise we need to apply the configuration by constructing the configurable url parameters
        //and regenerating the image urls
        this._isDefaultConfiguration = false;
        this._isCustomConfiguration = true;

        this._configuration = configuration;

        configurationUrl = this._constructConfigurableUrlParameters(configuration);

        this._imagePaths = this._constructViewUrls(this._validLatitudes, this._validLongitudes, this._compositors,
            this._compositorExtension, configurationUrl);

        this._previewImages = this._constructConfigurablePreviewImageUrls(this._data.assets.previews, this._compositors,
            this._previewImageSize, this._compositorExtension, configurationUrl, this._validLatitudes, this._validLongitudes);
    };

    AtlasAPIImageAdapter.prototype.getImagePath = function(imageIndex, resolution) {
        var path = this._imagePaths[imageIndex];

        if (!path) {
            var closest = this.getValidImageIndex(imageIndex);
            path = this._imagePaths[closest];
            return path;
        }

        return path;
    };

    AtlasAPIImageAdapter.prototype.getAvailableResolutionsCount = function() {
        return 1;
    };

    AtlasAPIImageAdapter.prototype.getLatitudes = function() {
        return this._latitudes;
    };

    AtlasAPIImageAdapter.prototype.getLongitudes = function() {
        return this._longitudes;
    };

    AtlasAPIImageAdapter.prototype.getValidLatitudes = function() {
        return this._validLatitudes;
    };

    AtlasAPIImageAdapter.prototype.getValidLongitudes = function() {
        return this._validLongitudes;
    };

    AtlasAPIImageAdapter.prototype.getPreviewImages = function() {
        return this._previewImages;
    };

    AtlasAPIImageAdapter.prototype.getStatic2dPreviewImages = function() {
        return this._static2dPreviewImages;
    };

    AtlasAPIImageAdapter.prototype.getShotPreviewImages = function() {
        return this._shotPreviewImages ? this._shotPreviewImages.previews : [];
    };

    AtlasAPIImageAdapter.prototype.getSmallImageForShot = function(tag) {
        if (!this._smallImages) {
            return null;
        }

        return this._smallImages.shots[tag];
    };

    AtlasAPIImageAdapter.prototype.getSmallImageForView = function(view) {
        if (!this._smallImages) {
            return null;
        }

        return this._smallImages.atlas[view];
    };

    AtlasAPIImageAdapter.prototype.getLargeImageForShot = function(tag) {
        if (this._shotPreviewImages && this._shotPreviewImages.resolutions && this._shotPreviewImages.resolutions[tag]) {
            return this._shotPreviewImages.resolutions[tag].largeUrl + '?modifiedAt=' + this._modifiedAt;
        }

        return null;
    };

    AtlasAPIImageAdapter.prototype.getDownloadFormatsForShot = function(tag) {
        if (this._shotPreviewImages && this._shotPreviewImages.resolutions && this._shotPreviewImages.resolutions[tag]) {
            return this._shotPreviewImages.resolutions[tag].available;
        }
        return null;
    };

    AtlasAPIImageAdapter.prototype.isValidDownloadForShot = function(tag, format, resolution) {
        if (this._shotPreviewImages && this._shotPreviewImages.resolutions && this._shotPreviewImages.resolutions[tag]) {
            var available = this._shotPreviewImages.resolutions[tag].available;
            for (var a = 0; a < available.length; ++a) {
                if (available[a].format === format && available[a].resolution === resolution) {
                    return true;
                }
            }
        }
        return false;
    };

    AtlasAPIImageAdapter.prototype.getDownloadFormats = function() {
        return this._downloadFormats || this._pixelSquidIndexDownloadFormats;
    };

    AtlasAPIImageAdapter.prototype.getDownloadFormatsForView = function(view) {
        return this._downloadFormats[view] || this._pixelSquidIndexDownloadFormats;
    };

    AtlasAPIImageAdapter.prototype.getValidImageIndex = function(imageIndex) {
        if (imageIndex) {
            //split into lat and lon
            var latitude = imageIndex.substring(0, 1);
            var longitude = imageIndex.substring(1);
            var latitudeIndex = this._validLatitudes.indexOf(latitude);
            var longitudeIndex = this._validLongitudes.indexOf(longitude);

            if ((this._validLatitudes.indexOf(latitude) >= 0) && (this._validLongitudes.indexOf(longitude) >= 0)) {
                //the passed in image was valid so return it
                return imageIndex;
            }
            else if (this._validLongitudes.indexOf(longitude) >=0) {
                //we have a valid longitude, but invalid latitude, so pick something close
                var bestMatch = this._validLatitudes[0];
                var requestedCode = latitude.charCodeAt(0);
                var bestDistance = Math.abs(bestMatch.charCodeAt(0) - requestedCode);

                for (var i = 0; i < this._validLatitudes.length; ++i) {
                    var validCode = this._validLatitudes[i].charCodeAt(0);
                    if (bestDistance > Math.abs(requestedCode - validCode)) {
                        bestMatch = this._validLatitudes[i];
                        bestDistance = Math.abs(requestedCode - validCode);
                    }
                }

                return bestMatch + longitude;
            }
            else if (this._validLatitudes.indexOf(latitude) >=0) {
                //we have a valid latitude, but invalid longitude, so pick something close
                var testLongitude = (parseInt(longitude, 10) + 1) % (this._longitudes + 1);
                for (var i = 0; i < this._longitudes; ++i) {
                    testLongitude = (testLongitude === 0) ? 1 : testLongitude;

                    var stringLongitude = testLongitude < 10 ? '0' : '' + testLongitude.toString();
                    if (this._validLongitudes.indexOf(stringLongitude)) {
                        return latitude + stringLongitude;
                    }
                }
            }
        }
        return this._validLatitudes[0] + this._validLongitudes[0];
    };

    return AtlasAPIImageAdapter;
});
